import React, { useState, useRef } from "react";
import { uploadImage, uploadMultipleImages, readHandwriting, uploadFile } from "../services/api";
import "./ImageUploader.css";

const ImageUploader = ({ sessionId, onAnalysis, onClose }) => {
  const [images, setImages] = useState([]);
  const [mode, setMode] = useState("general");
  const [question, setQuestion] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + images.length > 5) { alert("Maximum 5 files allowed."); return; }
    const newImages = files.map((file) => ({
      file, preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null, name: file.name,
    }));
    setImages((prev) => [...prev, ...newImages].slice(0, 5));
    e.target.value = "";
  };

  const removeImage = (index) => {
    setImages((prev) => {
      const updated = [...prev];
      if (updated[index].preview) URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const startCamera = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      alert("Camera requires HTTPS or localhost."); return;
    }
    setShowCamera(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play(); }
    } catch (err) { console.log("Camera error:", err.message); alert("Camera access denied."); setShowCamera(false); }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(videoRef.current, 0, 0);
    canvas.toBlob((blob) => {
      const file = new File([blob], "camera-capture-" + Date.now() + ".png", { type: "image/png" });
      setImages((prev) => [...prev, { file, preview: URL.createObjectURL(file), name: "Camera Capture" }].slice(0, 5));
      stopCamera();
    }, "image/png");
  };

  const stopCamera = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setShowCamera(false);
  };

  const handleSubmit = async () => {
    if (images.length === 0) return;
    setIsUploading(true);
    try {
      let result;
      const file = images[0].file;
      const isImage = file.type.startsWith("image/");

      if (!isImage) {
        result = await uploadFile(file, sessionId);
      } else if (mode === "handwriting") {
        result = await readHandwriting(file, sessionId);
      } else if (images.length === 1) {
        result = await uploadImage(file, mode, question || null, sessionId);
      } else {
        result = await uploadMultipleImages(images.map(i => i.file), mode, sessionId);
      }

      if (result.success) {
        onAnalysis(result.analysis || result.transcription, mode, images.length);
        images.forEach(i => { if (i.preview) URL.revokeObjectURL(i.preview); });
        setImages([]);
      }
    } catch (error) { console.error("Upload failed:", error); }
    finally { setIsUploading(false); }
  };

  return (
    <div className="image-uploader">
      <div className="uploader-header">
        <span className="uploader-title"><i className="fas fa-image"></i>Upload & Analyze</span>
        <button className="close-btn" onClick={onClose}><i className="fas fa-times"></i></button>
      </div>

      <div className="uploader-modes">
        <button className={"mode-select " + (mode === "general" ? "active" : "")} onClick={() => setMode("general")}>
          <i className="fas fa-eye"></i><span>Analyze</span>
        </button>
        <button className={"mode-select " + (mode === "handwriting" ? "active" : "")} onClick={() => setMode("handwriting")}>
          <i className="fas fa-pen-fancy"></i><span>Handwriting</span>
        </button>
        <button className={"mode-select " + (mode === "truth" ? "active" : "")} onClick={() => setMode("truth")}>
          <i className="fas fa-infinity"></i><span>Truth</span>
        </button>
      </div>

      {showCamera && (
        <div className="camera-container">
          <video ref={videoRef} className="camera-video" autoPlay playsInline />
          <div className="camera-controls">
            <button className="capture-btn" onClick={capturePhoto}><i className="fas fa-camera"></i></button>
            <button className="cancel-camera-btn" onClick={stopCamera}><i className="fas fa-times"></i></button>
          </div>
        </div>
      )}

      {images.length > 0 && (
        <div className="uploader-previews">
          {images.map((img, index) => (
            <div key={index} className="preview-item">
              {img.preview ? <img src={img.preview} alt={img.name} /> : <div className="file-icon"><i className="fas fa-file-alt"></i></div>}
              <button className="preview-remove" onClick={() => removeImage(index)}><i className="fas fa-times"></i></button>
              <span className="preview-name">{img.name}</span>
            </div>
          ))}
        </div>
      )}

      {mode !== "handwriting" && images.length > 0 && (
        <input type="text" value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Ask about these... (optional)" className="uploader-question" />
      )}

      <div className="uploader-actions">
        <input ref={fileInputRef} type="file" accept="image/*,.pdf,.doc,.docx,.txt,.csv,.json,.xml,.md,.log,.py,.js,.html,.css,.sh,.bash,.yml,.yaml,.toml,.ini,.cfg,.zip,.tar,.gz" multiple onChange={handleFileSelect} className="file-input-hidden" id="file-input" />
        <label htmlFor="file-input" className="select-files-btn">
          <i className="fas fa-folder-open"></i>
          <span>{images.length > 0 ? "Add More" : "Select Files"}</span>
          <span className="file-limit">{images.length}/5</span>
        </label>

        <button className="camera-btn" onClick={startCamera} title="Take a photo">
          <i className="fas fa-camera"></i><span>Camera</span>
        </button>

        <button className="analyze-btn" onClick={handleSubmit} disabled={images.length === 0 || isUploading}>
          {isUploading ? <><i className="fas fa-spinner fa-spin"></i><span>Analyzing...</span></> : <><i className="fas fa-search"></i><span>Analyze</span></>}
        </button>
      </div>
    </div>
  );
};

export default ImageUploader;
